import { PrescriptionExportApi, RemoteSensingApi } from '@core/api';
import { Property } from '@core/models/properties';
import { IPrescription, PrescriptionZone } from '@screens/Prescriptions/utils/prescriptions.models';
import { Field } from '@state/regions/region.models';
import { FeatureCollection, MultiPolygon, Polygon, Properties } from '@turf/turf';
import { DATE_FORMATS } from '@utils/dateUtils';
import { getDnRemapFromZones } from '@utils/prescriptions.utils';
import dayjs from 'dayjs';
import { IPrescriptionDetail } from './usePrescriptionDetail';

export const EXPORTERS = ['shapefile', 'simplified_shapefile', 'tiff', 'csv'] as const;

export type ExporterType = typeof EXPORTERS[number];

export function getGeojsonRx(
  prescription: IPrescription,
  details: IPrescriptionDetail,
  exportMethod: ExporterType
) {
  const { products } = prescription;
  const { zones, unitSystem } = details;
  const units = `${products[0].units} / ${unitSystem.areaUnit}`;
  const dnRemap = exportMethod === 'tiff' ? getDnRemapFromZones(zones) : null;

  const mappedFeatures = zones.map<
    Omit<PrescriptionZone, 'properties'> & {
      properties: { units: string; product: string; rate: number; color?: string; DN?: number };
    }
  >((feature) => {
    const { color, DN, rate } = feature.properties;
    const mappedDN = dnRemap?.get(DN) ?? DN;

    return {
      ...feature,
      properties: {
        units,
        product: products[0].name,
        rate,
        ...(exportMethod === 'tiff' && { color, DN: mappedDN }),
      },
    };
  });

  if (exportMethod === 'tiff') {
    mappedFeatures.sort((a, b) => (a.properties.DN ?? 0) - (b.properties.DN ?? 0));
  }

  return {
    type: 'FeatureCollection' as const,
    features: mappedFeatures,
  };
}

export function normalizeTiffFeatureCollection(
  rx: FeatureCollection<Polygon | MultiPolygon, Properties>
): FeatureCollection<Polygon | MultiPolygon, Properties> {
  const dnRemap = getDnRemapFromZones(rx.features);

  const features = rx.features
    .map((feature) => {
      const dn = feature.properties?.DN;

      return {
        ...feature,
        properties: { ...feature.properties, DN: dn == null ? dn : dnRemap.get(dn) },
      };
    })
    .sort((a, b) => (a.properties.DN ?? 0) - (b.properties.DN ?? 0));

  return { ...rx, features };
}

export function getNonEncodedFilename(
  fields: Field[],
  property?: Property,
  suffix?: string,
  max: number = 120
) {
  const nameSuffix = suffix ?? dayjs().format(DATE_FORMATS.USA_FORMAT);

  const fieldName = fields[0]?.name?.replaceAll(/[{()}]/g, '');
  const propertyName = property ? property.name.replaceAll(/[{()}]/g, '') + '_' : '';
  return `${propertyName}${fieldName}_${nameSuffix}`.substring(0, max).replaceAll('.', '_');
}

export async function getSignedLink(
  rx:
    | {
        type: 'FeatureCollection';
        features: (Omit<PrescriptionZone, 'properties'> & {
          properties: {
            units: string;
            product: string;
            rate: number;
            color?: string;
            DN?: number;
          };
        })[];
      }
    | FeatureCollection<Polygon | MultiPolygon, Properties>,
  exportMethod: ExporterType,
  filename?: string
) {
  const { data: res } = await RemoteSensingApi.post<{ signed_link: string }>(
    `/convert/geojson/to/${exportMethod === 'simplified_shapefile' ? 'shapefile' : exportMethod}`,
    { rx, ...(filename && { filename }) }
  );

  return res.signed_link;
}

export async function downloadZipFromSignedLink(
  signedLink: string,
  exportMethod?: ExporterType,
  filename?: string
) {
  let link = signedLink;

  if (exportMethod === 'simplified_shapefile') {
    const response = await PrescriptionExportApi.post('/qa/prescription/convert', {
      signed_link: signedLink,
      ...(filename && { filename }),
    });
    const convertedLink = response?.data?.signed_link;

    if (convertedLink) {
      link = convertedLink;
    }
  }

  const response = await fetch(link);
  const blob = await response.blob();

  return URL.createObjectURL(new Blob([blob], { type: 'application/zip' }));
}

export function downloadFile(url: string, filename?: string) {
  const element = document.createElement('a');
  document.body.appendChild(element);

  if (filename) {
    element.download = filename;
  }

  element.href = url;
  element.click();

  element.remove();

  URL.revokeObjectURL(url);
}
